import { z } from 'zod';
import i18n from '@/i18n';
import { isPhoneValid } from '@/utils/phone';
import {
  OCCASIONS_ENUM,
  DIETARY_ENUM,
} from '@/constants/reservationEnums';

// Helper to get current translation
const t = (key: string) => i18n.t(key);

export interface CustomerInfoSchemaOptions {
  /** When true, guest must accept the reservation policy checkbox */
  requirePolicyAcceptance?: boolean;
}

/**
 * Factory function to create customer information validation schema
 * Call this when you need fresh translations (e.g., when language changes)
 */
export const createCustomerInfoSchema = (options: CustomerInfoSchemaOptions = {}) => {
  const { requirePolicyAcceptance = true } = options;

  return z.object({
    firstName: z
      .string()
      .min(2, { message: t('validation.firstNameMinLength') })
      .regex(/^[A-Za-zΑ-Ωα-ωά-ώ\s]+$/, { message: t('validation.firstNameLettersOnly') }),

    lastName: z
      .string()
      .min(2, { message: t('validation.lastNameMinLength') })
      .regex(/^[A-Za-zΑ-Ωα-ωά-ώ\s]+$/, { message: t('validation.lastNameLettersOnly') }),

    email: z
      .string()
      .min(1, { message: t('validation.emailRequired') })
      .email({ message: t('validation.emailInvalid') }),

    phoneNumber: z
      .string()
      .min(1, { message: t('validation.phoneRequired') })
      .refine(isPhoneValid, { message: t('validation.phoneInvalid') }),

    specialOccasion: z
      .union([z.enum(OCCASIONS_ENUM as unknown as [string, ...string[]]), z.literal('')])
      .optional(),

    dietaryRestrictions: z
      .array(z.enum(DIETARY_ENUM as unknown as [string, ...string[]]))
      .optional()
      .default([]),

    note: z.string().max(500, { message: t('validation.noteMaxLength') }).optional(),

    acceptDuration: z.boolean().default(false),

    acceptTermsAndCancellation: requirePolicyAcceptance
      ? z
          .boolean()
          .refine((val) => val === true, { message: t('validation.termsAndPolicyRequired') })
      : z.boolean().default(true),
  });
};

// Default schema for backward compatibility
export const customerInfoSchema = createCustomerInfoSchema();

export type CustomerInfoFormData = z.infer<ReturnType<typeof createCustomerInfoSchema>>;
